import { Phase, PlayerStatus } from '@infiltre/shared';
import { selectRoom } from '@/entities/room';
import { RoundResult, WORD_LABEL_KEYS } from '@/features/game-round';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { AsyncStatus } from '@/shared/lib/async';
import { type MessageKey, useT } from '@/shared/lib/i18n';

/** Spectator view for eliminated viewers and post-game finished state. */
export const OnlineSpectatorOrchestrator = () => {
  const t = useT();
  const room = useGameStore(selectRoom);
  const identity = useSessionStore((s) => s.identity);

  if (!room || !identity) return null;
  const wordLabelKeys = WORD_LABEL_KEYS;

  if (room.phase === Phase.Finished) {
    return (
      <div className="flex h-full flex-col">
        <RoundResult
          room={room}
          eliminated={
            room.eliminatedThisRound
              ? (room.players.find((p) => p.id === room.eliminatedThisRound) ?? null)
              : null
          }
          onContinue={() => {
            // No-op: navigation back home is owned by the round-result path.
          }}
          continueLabel={t('round.back_home')}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <main className="mx-auto flex h-full w-full max-w-md flex-col px-5 pt-2 pb-5">
        <header>
          <div className="font-bold font-display text-[10.5px] text-und uppercase tracking-[0.22em]">
            {t('online.spectator.eyebrow')}
          </div>
          <h1 className="mt-1.5 font-display font-extrabold text-[36px] text-ink leading-[0.95] sm:text-[44px]">
            {t('online.spectator.heading')}.
          </h1>
          <p className="mt-2 text-[14px] text-text-dim italic leading-[1.45]">
            {t('online.spectator.eliminated')}
          </p>
        </header>

        {wordLabelKeys.primary && wordLabelKeys.secondary && room.flicWord && room.infiltreWord && (
          <div className="mt-5 grid grid-cols-2 gap-2.5">
            <div className="rounded-2xl border-2 border-ink bg-cream p-3 shadow-[3px_3px_0_var(--color-ink)]">
              <div className="eyebrow text-[10px] text-text-dim tracking-[0.18em]">
                {t(wordLabelKeys.primary)}
              </div>
              <div className="mt-1 font-display font-extrabold text-[18px] text-ink">
                {room.flicWord}
              </div>
            </div>
            <div className="rounded-2xl border-2 border-ink bg-cream p-3 shadow-[3px_3px_0_var(--color-ink)]">
              <div className="eyebrow text-[10px] text-und tracking-[0.18em]">
                {t(wordLabelKeys.secondary)}
              </div>
              <div className="mt-1 font-display font-extrabold text-[18px] text-ink">
                {room.infiltreWord}
              </div>
            </div>
          </div>
        )}

        <div className="eyebrow mt-6 text-[10px] text-text-dim tracking-[0.18em]">THE TABLE</div>
        <ul className="mt-2 flex flex-col gap-2">
          {room.players.map((p) => (
            <li
              key={p.id}
              className={[
                'flex items-center justify-between rounded-xl border border-border bg-surface px-4 py-2.5',
                p.status === PlayerStatus.Eliminated ? 'opacity-55' : '',
              ].join(' ')}
            >
              <span className={p.status === PlayerStatus.Eliminated ? 'line-through' : ''}>
                {p.name}
              </span>
              {p.role && (
                <span className="eyebrow text-[10px] text-text-dim tracking-[0.16em]">
                  {t(`role.${p.role}` as MessageKey)}
                </span>
              )}
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
};
